import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function ContactForm() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('contact.form.validation.firstNameRequired');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('contact.form.validation.lastNameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.validation.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('contact.form.validation.emailInvalid');
    }
    if (!formData.subject) {
      newErrors.subject = t('contact.form.validation.subjectRequired');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.validation.messageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('contact.form.validation.messageMin');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);

      const response = await fetch('https://readdy.ai/api/form/d5v60ams8bb84h5iene0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleReset = () => {
    setSubmitStatus('idle');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      subject: '',
      message: '',
    });
    setErrors({});
  };

  if (submitStatus === 'success') {
    return (
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 p-12 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-500/10 rounded-full mx-auto mb-6">
              <i className="ri-checkbox-circle-line text-5xl text-green-500"></i>
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">{t('contact.form.success.title')}</h3>
            <p className="text-gray-300 text-lg mb-8">{t('contact.form.success.message')}</p>
            <button
              onClick={handleReset}
              className="px-8 py-3 bg-yellow-400 text-black rounded-lg font-medium hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('contact.form.success.button')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('contact.form.title')}</h2>
          <p className="text-gray-400 text-lg">{t('contact.form.subtitle')}</p>
        </div>

        <div className="bg-gray-800 rounded-2xl border border-gray-700 p-8 lg:p-12">
          <form onSubmit={handleSubmit} data-readdy-form id="contact_form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('contact.form.fields.firstName')}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder={t('contact.form.fields.firstNamePlaceholder')}
                  className={`w-full px-4 py-3 bg-gray-700 border ${
                    errors.firstName ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors`}
                />
                {errors.firstName && (
                  <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('contact.form.fields.lastName')}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder={t('contact.form.fields.lastNamePlaceholder')}
                  className={`w-full px-4 py-3 bg-gray-700 border ${
                    errors.lastName ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors`}
                />
                {errors.lastName && (
                  <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t('contact.form.fields.email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('contact.form.fields.emailPlaceholder')}
                className={`w-full px-4 py-3 bg-gray-700 border ${
                  errors.email ? 'border-red-500' : 'border-gray-600'
                } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors`}
              />
              {errors.email && (
                <p className="text-red-400 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Subject */}
            <div className="mb-6">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                {t('contact.form.fields.subject')}
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-700 border ${
                  errors.subject ? 'border-red-500' : 'border-gray-600'
                } rounded-lg text-white focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors cursor-pointer`}
              >
                <option value="">{t('contact.form.fields.subjectPlaceholder')}</option>
                <option value="general">{t('contact.form.subjects.general')}</option>
                <option value="technical">{t('contact.form.subjects.technical')}</option>
                <option value="billing">{t('contact.form.subjects.billing')}</option>
                <option value="partnership">{t('contact.form.subjects.partnership')}</option>
                <option value="feedback">{t('contact.form.subjects.feedback')}</option>
                <option value="other">{t('contact.form.subjects.other')}</option>
              </select>
              {errors.subject && (
                <p className="text-red-400 text-sm mt-1">{errors.subject}</p>
              )}
            </div>

            {/* Message */}
            <div className="mb-8">
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                {t('contact.form.fields.message')}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t('contact.form.fields.messagePlaceholder')}
                rows={5}
                maxLength={500}
                className={`w-full px-4 py-3 bg-gray-700 border ${
                  errors.message ? 'border-red-500' : 'border-gray-600'
                } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors resize-none`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.message ? (
                  <p className="text-red-400 text-sm">{errors.message}</p>
                ) : (
                  <span></span>
                )}
                <p className="text-gray-400 text-sm">{formData.message.length}/500</p>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-yellow-400 text-black rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  {t('contact.form.submitting')}
                </span>
              ) : (
                t('contact.form.submit')
              )}
            </button>

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-500/10 border border-red-500 rounded-lg">
                <p className="text-red-400 text-center">{t('contact.form.error.message')}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
